import React, { useState, useMemo, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Search, ShoppingCart, Plus, Minus, Trash2, CreditCard, Banknote,
  QrCode, X, User, Printer, RotateCcw, Gift, UserPlus, LayoutGrid,
  List, Coffee, ChevronDown, Edit3, Zap, Bell, RefreshCw, Menu,
  DoorOpen, Package, UtensilsCrossed, GlassWater, ShoppingBag,
} from "lucide-react";

const ROOM_CATEGORIES = [
  { key: "all", label: "Tất cả" },
  { key: "tai_ban_kr", label: "TẠI BÀN KR" },
  { key: "tai_ban_pp", label: "TẠI BÀN PP" },
  { key: "mang_di", label: "Khách đoàn / Mang đi" },
  { key: "karinox", label: "KARINOX ARENA" },
];

const PAYMENT_METHODS = [
  { key: "CASH", label: "Tiền mặt", icon: Banknote },
  { key: "BANK_TRANSFER", label: "Chuyển khoản", icon: CreditCard },
  { key: "MOMO", label: "Momo", icon: QrCode },
  { key: "VNPAY", label: "VNPay", icon: Zap },
  { key: "CARD", label: "Quẹt thẻ", icon: CreditCard },
  { key: "GRAB", label: "Grab", icon: ShoppingBag },
];

export default function POSPage() {
  const [activeTab, setActiveTab] = useState("menu"); // "rooms" | "menu"
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("");
  const [activeRoom, setActiveRoom] = useState("all");
  const [roomFilter, setRoomFilter] = useState("all"); // all | using | empty
  const [viewMode, setViewMode] = useState("grid"); // grid | list
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [payMethod, setPayMethod] = useState("CASH");
  const [amountReceived, setAmountReceived] = useState("");
  const [paying, setPaying] = useState(false);
  const [holdOrders, setHoldOrders] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const searchRef = useRef(null);

  // Fetch data
  const { data: categoriesData } = useQuery({
    queryKey: ["pos-categories"],
    queryFn: () => api.get("/products/categories").then(r => r.data.categories || []),
  });
  const categories = categoriesData || [];

  const { data: productsData } = useQuery({
    queryKey: ["pos-products"],
    queryFn: () => api.get("/products").then(r => r.data.products || []),
  });
  const products = productsData || [];

  // Filtered products
  const filtered = useMemo(() => {
    let list = products;
    if (activeCat) list = list.filter(p => p.category_id === activeCat || p.group === activeCat);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => (p.name || "").toLowerCase().includes(q) || (p.code || "").toLowerCase().includes(q));
    }
    return list;
  }, [products, activeCat, search]);

  // Cart logic
  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);

  const addToCart = useCallback((product) => {
    setCart(prev => {
      const idx = prev.findIndex(i => i.product_id === (product.id || product._id));
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], qty: updated[idx].qty + 1 };
        return updated;
      }
      return [...prev, {
        product_id: product.id || product._id,
        name: product.name,
        price: product.price || 0,
        qty: 1,
        unit: product.unit || "Ly",
      }];
    });
    if (activeTab === "rooms") setActiveTab("menu");
  }, [activeTab]);

  const updateQty = useCallback((productId, delta) => {
    setCart(prev => prev.map(i => {
      if (i.product_id === productId) {
        const newQty = i.qty + delta;
        return newQty > 0 ? { ...i, qty: newQty } : null;
      }
      return i;
    }).filter(Boolean));
  }, []);

  const removeItem = useCallback((productId) => {
    setCart(prev => prev.filter(i => i.product_id !== productId));
  }, []);

  const clearCart = useCallback(() => { setCart([]); setCustomerName(""); setCustomerPhone(""); setSelectedTable(null); }, []);

  // Hold order
  const holdOrder = useCallback(() => {
    if (cart.length === 0) return;
    setHoldOrders(prev => [...prev, { id: Date.now(), items: cart, table: selectedTable, customer: customerName, time: new Date().toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" }) }]);
    clearCart();
    toast.success("Đã tạm giữ đơn");
  }, [cart, selectedTable, customerName, clearCart]);

  const restoreOrder = useCallback((orderId) => {
    const order = holdOrders.find(o => o.id === orderId);
    if (order) {
      setCart(order.items);
      setSelectedTable(order.table);
      setCustomerName(order.customer || "");
      setHoldOrders(prev => prev.filter(o => o.id !== orderId));
    }
  }, [holdOrders]);

  // Payment
  const handlePay = useCallback(async () => {
    if (cart.length === 0) { toast.error("Giỏ hàng trống"); return; }
    setPaying(true);
    try {
      await api.post("/pos/orders", {
        customer_name: customerName,
        customer_phone: customerPhone,
        items: cart.map(i => ({ item_type: "PRODUCT", product_id: i.product_id, name: i.name, qty: i.qty, price: i.price })),
      });
      const orderId = `HD${Date.now().toString().slice(-6)}`;
      await api.post(`/pos/orders/${orderId}/pay`, { method: payMethod, amount_received: parseFloat(amountReceived) || cartTotal });
      toast.success("Thanh toán thành công!");
      clearCart();
      setPayOpen(false);
      setAmountReceived("");
    } catch (err) {
      toast.error("Lỗi thanh toán");
    } finally { setPaying(false); }
  }, [cart, customerName, customerPhone, payMethod, amountReceived, cartTotal, clearCart]);

  // Generate tables
  const tables = useMemo(() => {
    const rooms = [];
    const configs = [
      { prefix: "KR", count: 30, category: "tai_ban_kr" },
      { prefix: "PP", count: 20, category: "tai_ban_pp" },
      { prefix: "MD", count: 10, category: "mang_di" },
      { prefix: "AR", count: 8, category: "karinox" },
    ];
    configs.forEach(c => {
      for (let i = 1; i <= c.count; i++) {
        rooms.push({ id: `${c.prefix}${i}`, name: `${c.prefix} ${i}`, category: c.category, status: "empty" });
      }
    });
    return rooms;
  }, []);

  const filteredTables = useMemo(() => {
    let list = tables;
    if (activeRoom !== "all") list = list.filter(t => t.category === activeRoom);
    if (roomFilter === "using") list = list.filter(t => t.status === "using");
    if (roomFilter === "empty") list = list.filter(t => t.status === "empty");
    return list;
  }, [tables, activeRoom, roomFilter]);

  const totalTables = activeRoom === "all" ? tables.length : tables.filter(t => t.category === activeRoom).length;

  return (
    <div className="h-screen flex flex-col bg-[#f0f2f5] overflow-hidden" data-testid="pos-page">
      {/* ═══ TOP NAVBAR (KiotViet style - dark blue) ═══ */}
      <div className="h-12 bg-[#1a3a5c] flex items-center px-3 gap-2 shrink-0">
        {/* Tabs */}
        <button onClick={() => setActiveTab("rooms")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${activeTab === "rooms" ? "bg-white/10 text-white" : "text-white/60 hover:text-white/80"}`}>
          <DoorOpen className="w-3.5 h-3.5" /> Phòng bàn
        </button>
        <button onClick={() => setActiveTab("menu")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${activeTab === "menu" ? "bg-[#2196F3] text-white" : "text-white/60 hover:text-white/80"}`}>
          <UtensilsCrossed className="w-3.5 h-3.5" /> Thực đơn
        </button>

        {/* Search bar */}
        <div className="flex-1 max-w-md mx-3 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
          <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)} placeholder="Tìm món (F3)"
            className="w-full h-8 pl-8 pr-3 rounded bg-white/10 border border-white/20 text-white text-xs placeholder:text-white/40 focus:outline-none focus:bg-white/15 focus:border-white/40" />
        </div>

        {/* Action buttons */}
        <button className="w-8 h-8 rounded bg-[#2196F3] flex items-center justify-center text-white hover:bg-[#1e88e5]" title="Đồng bộ"><Zap className="w-3.5 h-3.5" /></button>
        <button className="w-8 h-8 rounded flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10" title="Thêm đơn"><Plus className="w-4 h-4" /></button>

        <div className="w-px h-6 bg-white/20 mx-1"></div>

        {/* Right icons */}
        <button className="w-7 h-7 rounded flex items-center justify-center text-white/60 hover:text-white" title="Thông báo"><Bell className="w-3.5 h-3.5" /></button>
        <button className="w-7 h-7 rounded flex items-center justify-center text-white/60 hover:text-white" title="In"><Printer className="w-3.5 h-3.5" /></button>
        <button className="w-7 h-7 rounded flex items-center justify-center text-white/60 hover:text-white" title="Đồng bộ"><RefreshCw className="w-3.5 h-3.5" /></button>

        <div className="w-px h-6 bg-white/20 mx-1"></div>
        <span className="text-white/80 text-[10px] font-medium">KARINOX</span>
        <button className="w-7 h-7 rounded flex items-center justify-center text-white/60 hover:text-white"><Menu className="w-3.5 h-3.5" /></button>
      </div>

      {/* ═══ MAIN CONTENT ═══ */}
      <div className="flex-1 flex overflow-hidden">
        {/* ═══ LEFT PANEL (Products/Tables) ═══ */}
        <div className="flex-1 flex flex-col overflow-hidden border-r border-[#e0e0e0]">
          {/* Category tabs */}
          {activeTab === "menu" ? (
            <>
              <div className="h-10 bg-white border-b border-[#e0e0e0] flex items-center px-3 gap-1 overflow-x-auto shrink-0">
                <button onClick={() => setActiveCat("")} className={`px-3 py-1.5 rounded text-[11px] font-medium whitespace-nowrap transition-colors ${!activeCat ? "bg-[#2196F3] text-white" : "text-[#555] hover:bg-[#e8e8e8]"}`}>Tất cả</button>
                {categories.map(c => (
                  <button key={c.id || c.name} onClick={() => setActiveCat(c.id || c.name)}
                    className={`px-3 py-1.5 rounded text-[11px] font-medium whitespace-nowrap transition-colors ${activeCat === (c.id || c.name) ? "bg-[#2196F3] text-white" : "text-[#555] hover:bg-[#e8e8e8]"}`}>
                    {c.name}
                  </button>
                ))}
              </div>

              {/* Product grid */}
              <div className="flex-1 overflow-y-auto p-3 bg-[#f5f5f5]">
                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-[#999]">
                    <Package className="w-12 h-12 mb-2" />
                    <p className="text-sm">Không tìm thấy sản phẩm</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                    {filtered.map(p => (
                      <div key={p.id || p._id} onClick={() => addToCart(p)} data-testid={`pos-product-${p.id || p._id}`}
                        className="bg-white rounded-lg border border-[#e8e8e8] overflow-hidden cursor-pointer hover:shadow-md hover:border-[#2196F3]/50 transition-all group">
                        <div className="aspect-square bg-[#f8f9fa] flex items-center justify-center relative overflow-hidden">
                          {p.image_url ? (
                            <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            <Coffee className="w-8 h-8 text-[#ccc]" />
                          )}
                          <div className="absolute inset-0 bg-[#2196F3]/0 group-hover:bg-[#2196F3]/10 transition-colors flex items-center justify-center">
                            <Plus className="w-6 h-6 text-[#2196F3] opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                        <div className="p-2">
                          <p className="text-[11px] text-[#333] font-medium leading-tight line-clamp-2 min-h-[28px]">{p.name}</p>
                          <p className="text-[11px] text-[#2196F3] font-bold mt-1">{formatVND(p.price || 0)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* ═══ ROOMS/TABLES VIEW ═══ */
            <>
              <div className="h-10 bg-white border-b border-[#e0e0e0] flex items-center px-3 gap-2 shrink-0">
                {ROOM_CATEGORIES.map(r => (
                  <button key={r.key} onClick={() => setActiveRoom(r.key)}
                    className={`px-3 py-1.5 rounded text-[11px] font-medium whitespace-nowrap transition-colors ${activeRoom === r.key ? "bg-[#2196F3] text-white" : "text-[#555] hover:bg-[#e8e8e8]"}`}>
                    {r.label}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={() => setViewMode("grid")} className={`p-1 rounded ${viewMode === "grid" ? "text-[#2196F3]" : "text-[#999]"}`}><LayoutGrid className="w-4 h-4" /></button>
                  <button onClick={() => setViewMode("list")} className={`p-1 rounded ${viewMode === "list" ? "text-[#2196F3]" : "text-[#999]"}`}><List className="w-4 h-4" /></button>
                </div>
              </div>

              {/* Radio filters */}
              <div className="h-8 bg-white border-b border-[#e0e0e0] flex items-center px-3 gap-4 shrink-0">
                {[{ key: "all", label: `Tất cả (${totalTables})` }, { key: "using", label: "Sử dụng (0)" }, { key: "empty", label: `Còn trống (${totalTables})` }].map(f => (
                  <label key={f.key} className="flex items-center gap-1.5 text-[11px] text-[#555] cursor-pointer">
                    <input type="radio" name="roomFilter" checked={roomFilter === f.key} onChange={() => setRoomFilter(f.key)} className="w-3 h-3 accent-[#2196F3]" />
                    {f.label}
                  </label>
                ))}
              </div>

              {/* Tables grid */}
              <div className="flex-1 overflow-y-auto p-3 bg-[#f5f5f5]">
                <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
                  {filteredTables.map(t => (
                    <div key={t.id} onClick={() => { setSelectedTable(t); setActiveTab("menu"); }}
                      className={`aspect-square rounded-lg border-2 flex flex-col items-center justify-center cursor-pointer transition-all hover:shadow-md ${selectedTable?.id === t.id ? "border-[#2196F3] bg-[#2196F3]/5" : "border-[#e0e0e0] bg-white hover:border-[#2196F3]/50"}`}>
                      <DoorOpen className={`w-5 h-5 mb-1 ${t.status === "using" ? "text-orange-500" : "text-[#999]"}`} />
                      <span className="text-[10px] font-medium text-[#333]">{t.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Bottom bar */}
          <div className="h-8 bg-white border-t border-[#e0e0e0] flex items-center px-3 text-[10px] text-[#999] gap-4 shrink-0">
            <span>KARINOX Arena</span>
            <span>|</span>
            <span>Phím tắt: F3 Tìm món</span>
          </div>
        </div>

        {/* ═══ RIGHT PANEL (Cart/Order) ═══ */}
        <div className="w-[380px] flex flex-col bg-white shrink-0">
          {/* Cart header */}
          <div className="h-10 border-b border-[#e0e0e0] flex items-center justify-between px-3 shrink-0">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-[#2196F3]" />
              <span className="text-xs font-medium text-[#333]">Đơn hàng</span>
              {selectedTable && <span className="text-[10px] bg-[#2196F3]/10 text-[#2196F3] px-1.5 py-0.5 rounded font-medium">{selectedTable.name}</span>}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={holdOrder} className="p-1.5 rounded hover:bg-[#f0f0f0] text-[#666]" title="Tạm giữ đơn"><RotateCcw className="w-3.5 h-3.5" /></button>
              <button className="p-1.5 rounded hover:bg-[#f0f0f0] text-[#666]" title="Voucher"><Gift className="w-3.5 h-3.5" /></button>
              <button onClick={clearCart} className="p-1.5 rounded hover:bg-red-50 text-[#666] hover:text-red-500" title="Xoá đơn"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>

          {/* Customer info */}
          <div className="px-3 py-2 border-b border-[#f0f0f0] flex items-center gap-2">
            <User className="w-3.5 h-3.5 text-[#999]" />
            <input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Tên khách hàng" className="flex-1 text-xs text-[#333] placeholder:text-[#bbb] bg-transparent outline-none" />
            <button className="p-1 rounded hover:bg-[#f0f0f0]"><UserPlus className="w-3.5 h-3.5 text-[#999]" /></button>
          </div>

          {/* Held orders */}
          {holdOrders.length > 0 && (
            <div className="px-3 py-1.5 border-b border-[#f0f0f0] bg-amber-50">
              <p className="text-[9px] text-amber-600 font-medium mb-1">Đơn tạm giữ ({holdOrders.length})</p>
              <div className="flex gap-1 overflow-x-auto">
                {holdOrders.map(o => (
                  <button key={o.id} onClick={() => restoreOrder(o.id)} className="px-2 py-1 bg-white border border-amber-200 rounded text-[9px] text-amber-700 hover:bg-amber-100 whitespace-nowrap">
                    {o.table?.name || "Đơn"} ({o.items.length}) - {o.time}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Cart items */}
          <div className="flex-1 overflow-y-auto">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-[#ccc]">
                <ShoppingCart className="w-10 h-10 mb-2" />
                <p className="text-xs">Chưa có sản phẩm</p>
              </div>
            ) : (
              <div className="divide-y divide-[#f5f5f5]">
                {cart.map((item, i) => (
                  <div key={item.product_id} className="px-3 py-2.5 hover:bg-[#fafafa] group" data-testid={`cart-item-${i}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#333] font-medium truncate">{item.name}</p>
                        <p className="text-[10px] text-[#2196F3] font-medium mt-0.5">{formatVND(item.price)}</p>
                      </div>
                      <button onClick={() => removeItem(item.product_id)} className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 text-[#ccc] hover:text-red-500 transition-opacity"><X className="w-3 h-3" /></button>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="flex items-center gap-0 border border-[#e0e0e0] rounded overflow-hidden">
                        <button onClick={() => updateQty(item.product_id, -1)} className="w-6 h-6 flex items-center justify-center text-[#666] hover:bg-[#f0f0f0]"><Minus className="w-3 h-3" /></button>
                        <span className="w-8 h-6 flex items-center justify-center text-[11px] font-medium text-[#333] border-x border-[#e0e0e0]">{item.qty}</span>
                        <button onClick={() => updateQty(item.product_id, 1)} className="w-6 h-6 flex items-center justify-center text-[#666] hover:bg-[#f0f0f0]"><Plus className="w-3 h-3" /></button>
                      </div>
                      <span className="text-xs font-bold text-[#333]">{formatVND(item.price * item.qty)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cart footer / Payment */}
          <div className="border-t border-[#e0e0e0] shrink-0">
            {/* Summary */}
            <div className="px-3 py-2 space-y-1">
              <div className="flex justify-between text-[11px] text-[#666]">
                <span>Số lượng:</span><span>{cartCount} món</span>
              </div>
              <div className="flex justify-between text-[11px] text-[#666]">
                <span>Giảm giá:</span><span>0</span>
              </div>
            </div>
            {/* Total + Pay button */}
            <div className="px-3 py-3 bg-[#f8f9fa] flex items-center justify-between">
              <div>
                <p className="text-[10px] text-[#999] uppercase">Tổng tiền</p>
                <p className="text-xl font-bold text-[#2196F3]">{formatVND(cartTotal)}</p>
              </div>
              <Button onClick={() => cart.length > 0 && setPayOpen(true)} disabled={cart.length === 0}
                className="h-10 px-6 bg-[#2196F3] hover:bg-[#1e88e5] text-white font-medium text-sm rounded-lg" data-testid="pos-pay-btn">
                Thanh toán
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ PAYMENT DIALOG ═══ */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-white border-[#e0e0e0] max-w-md" data-testid="pos-payment-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#333] text-base">Thanh toán</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Order summary */}
            <div className="bg-[#f8f9fa] rounded-lg p-3">
              <div className="flex justify-between text-sm">
                <span className="text-[#666]">Tổng tiền:</span>
                <span className="font-bold text-[#333]">{formatVND(cartTotal)}</span>
              </div>
              {selectedTable && (
                <div className="flex justify-between text-xs mt-1 text-[#999]">
                  <span>Bàn:</span><span>{selectedTable.name}</span>
                </div>
              )}
            </div>

            {/* Payment methods */}
            <div>
              <p className="text-xs font-medium text-[#555] mb-2">Phương thức thanh toán</p>
              <div className="grid grid-cols-3 gap-2">
                {PAYMENT_METHODS.map(m => (
                  <button key={m.key} onClick={() => setPayMethod(m.key)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-colors ${payMethod === m.key ? "border-[#2196F3] bg-[#2196F3]/5" : "border-[#e0e0e0] hover:border-[#2196F3]/50"}`}>
                    <m.icon className={`w-5 h-5 ${payMethod === m.key ? "text-[#2196F3]" : "text-[#999]"}`} />
                    <span className={`text-[10px] font-medium ${payMethod === m.key ? "text-[#2196F3]" : "text-[#666]"}`}>{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Amount received (for cash) */}
            {payMethod === "CASH" && (
              <div>
                <p className="text-xs font-medium text-[#555] mb-1">Tiền khách đưa</p>
                <Input value={amountReceived} onChange={e => setAmountReceived(e.target.value)} placeholder={formatVND(cartTotal)}
                  className="border-[#e0e0e0] text-[#333] h-10" />
                {amountReceived && parseFloat(amountReceived) > cartTotal && (
                  <p className="text-xs text-[#2196F3] mt-1">Tiền thừa: {formatVND(parseFloat(amountReceived) - cartTotal)}</p>
                )}
              </div>
            )}

            {/* Pay button */}
            <Button onClick={handlePay} disabled={paying} className="w-full h-11 bg-[#2196F3] hover:bg-[#1e88e5] text-white font-medium text-sm" data-testid="pos-confirm-pay">
              {paying ? "Đang xử lý..." : `Xác nhận thanh toán ${formatVND(cartTotal)}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
